import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { DialogDeleteAccountComponent } from '@app/modules/settings/components/dialog-delete-account/dialog-delete-account.component';
import { DialogInvitePrimaryHostComponent } from '@app/modules/settings/components/dialog-invite-primary-host/dialog-invite-primary-host.component';
import { Channel, ChannelMeta, NotificationType, User } from '@app/shared/interfaces';
import {
  ChannelNotification,
  ChannelNotificationsService,
} from '@app/shared/services/channel-notifications/channel-notifications.service';
import { IntercomService } from '@app/shared/services/intercom.service';
import { InternalCsService } from '@app/shared/services/internal-cs/internal-cs.service';
import { SettingsService } from '@app/shared/services/settings/settings.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { DialogChannelNotificationsComponent } from '../../components/dialog-channel-notifications/dialog-channel-notifications.component';

interface connectBox {
  id: string;
  image: string;
  description: string;
  button: string;
  buttonClass?: string;
  buttonAction?: string;
  new?: boolean;
  hidden?: boolean;
}

@Component({
  standalone: false,
  selector: 'sbnb-connected-accounts-overview',
  templateUrl: './connected-accounts-overview.component.html',
  styleUrls: ['./connected-accounts-overview.component.scss'],
})
export class ConnectedAccountsOverviewComponent implements OnInit {
  connectBoxes: connectBox[];

  loadingConnectedAccounts: boolean;
  connectedAccounts: Channel[];

  isCsDebug$ = this.internalCsService.debugMode;

  currentPage = 1;

  searchCriteria: string;

  showBookingButton = true;

  user: User = this.authService.getUserDetails();
  airbnbOfficialToken: string;

  channelNotifications: { [key: number]: ChannelNotification[] } = {};

  constructor(
    private readonly settingsService: SettingsService,
    private readonly toastNotificationsService: ToastNotificationsService,
    private readonly dialog: MatDialog,
    private readonly internalCsService: InternalCsService,
    private readonly authService: AuthenticationService,
    private readonly intercomService: IntercomService,
    private readonly channelNotificationsService: ChannelNotificationsService
  ) {}

  ngOnInit(): void {
    // remove the login we saved locally if it exists. We only need this for sending property id for booking.com
    localStorage.removeItem('account_login');
    this.authService.getUserDetailsFromApi().subscribe((res) => {
      this.user = res;

      this.populateConnectBoxes();

      this.loadConnectedAccounts();
    });
  }

  searchUpdated(searchString: string) {
    this.searchCriteria = searchString;
  }

  populateConnectBoxes() {
    this.connectBoxes = [
      {
        id: 'airbnb',
        image: 'connected-accounts/airbnb.svg',
        description: `Connect your Airbnb account with Hospitable.`,
        button: 'Connect with Airbnb',
        buttonClass: 'bg-airbnb',
        buttonAction: 'new/airbnb',
      },
      {
        id: 'vrbo',
        image: 'connected-accounts/vrbo.svg',
        description: `Connect your Vrbo account with Hospitable.`,
        button: 'Connect with Vrbo',
        buttonClass: 'bg-vrbo',
        buttonAction: 'new/vrbo',
      },
      {
        id: 'booking',
        image: 'connected-accounts/booking.svg',
        description: `Connect your Booking.com account with Hospitable.`,
        button: 'Connect with Booking.com',
        buttonClass: 'bg-booking',
        buttonAction: 'new/booking',
      },
      {
        id: 'invite',
        image: 'connected-accounts/invite-hosts.svg',
        description: `Invite hosts to connect their Airbnb channels to your Hospitable account.`,
        button: 'Invite hosts',
        buttonAction: 'new/invite',
      },
    ];
  }

  loadConnectedAccounts() {
    this.loadingConnectedAccounts = true;

    this.settingsService.getAccounts().subscribe((res) => {
      this.loadingConnectedAccounts = false;

      this.connectedAccounts = res.data.map(this.setAccountPmsInvitationRequirementStatus);

      this.setAirbnbOfficial(res);
    });
  }

  setAirbnbOfficial(res: { data: Channel[]; meta: ChannelMeta }) {
    // Still default to the old flow
    this.airbnbOfficialToken = `${btoa(res.meta.airbnb_auth_url)}`;
    this.setAirbnbAction(`new/airbnb-official/${this.airbnbOfficialToken}`);

    if (res.meta.enable_pms_upgrade) {
      this.setAirbnbAction(`new/airbnb`);
      return;
    }

    // Airbnb PMS flow is enabled, we use the new/airbnb route
    this.airbnbOfficialToken = null;
    this.setAirbnbAction(`new/airbnb`);
  }

  private setAirbnbAction(action: string) {
    if (this.connectBoxes?.length) {
      this.connectBoxes[0].buttonAction = action;
    }
  }

  trackByAccount(index: number, account: any): number {
    return account.platform_id;
  }

  resync(resync_link: string, login: string) {
    this.settingsService.resyncAccount(resync_link).subscribe((res) => {
      this.toastNotificationsService.open(
        `Your account ${login ? login : ''} is being refreshed.`,
        'Dismiss',
        NotificationType.Error
      );
    });
  }

  delete(acc: Channel) {
    const dialogRef = this.dialog.open(DialogDeleteAccountComponent, {
      width: '600px',
      maxHeight: '90vh',
      data: {
        acc,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const index = this.connectedAccounts.findIndex((ac) => ac.platform_id === acc.platform_id);

        if (index !== -1) {
          this.connectedAccounts = this.connectedAccounts.filter((_, i) => i !== index);
        }
      }
    });
  }

  openIntercomHome() {
    this.intercomService.show();
  }

  openIntercom() {
    this.intercomService.showNewMessage('I have a question, before I connect my first channel.');
  }

  openInviteHostModal(account) {
    const dialogRef = this.dialog.open(DialogInvitePrimaryHostComponent, {
      width: '600px',
      maxHeight: '90vh',
      data: {
        account,
      },
    });
  }

  private setAccountPmsInvitationRequirementStatus(account: Channel) {
    const listingsThatRequirePmsUpgrade = account.co_host_only_listings.filter((l) => l.requires_pms_upgrade);

    if (account.co_host_only_listings.length === 0) {
      return account;
    }

    // Only if all the co-hosted listings require PMS upgrade
    if (account.scope_type === 'pms' && listingsThatRequirePmsUpgrade.length === account.co_host_only_listings.length) {
      account.requires_primary_host_pms_upgrade = true;
    }

    return account;
  }

  loadNotifications(channelId: number) {
    // Open dialog immediately
    const dialogRef = this.dialog.open(DialogChannelNotificationsComponent, {
      data: { notifications: [] },
    });

    // If we don't have notifications cached, fetch them
    if (!this.channelNotifications[channelId]) {
      this.channelNotificationsService.getChannelNotifications(channelId).subscribe(
        (notifications) => {
          this.channelNotifications[channelId] = notifications;
          // Update dialog data
          dialogRef.componentInstance.data = { notifications };
          dialogRef.componentInstance.groupNotifications();
        },
        (error) => {
          console.error('Error loading notifications:', error);
          this.toastNotificationsService.error('Error loading notifications');
          dialogRef.close();
        }
      );
    } else {
      // If we have cached notifications, update the dialog
      dialogRef.componentInstance.data = { notifications: this.channelNotifications[channelId] };
      dialogRef.componentInstance.groupNotifications();
    }
  }

  openInteractiveTour() {
    window.open('https://hospitable.storylane.io/share/drkxdtxiqwvg', '_blank');
  }
}
